import SiteCoreLinks from "@/components/SiteCoreLinks";
// /en/visa-wizard — EN Thai Visa Eligibility Wizard. Phase P-VISA-WIZARD (2026-06).
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MessageCircle, Sparkles, ArrowRight } from "lucide-react";
import { VisaWizard, WizardRelatedServices } from "@/components/VisaWizard";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/visa-wizard";
export const TITLE = "Which Thailand Visa Is Right for You? Eligibility Wizard 2026 | NYC";
export const DESC =
  "Answer 5 questions in 60 seconds — get a personalised Thai visa recommendation across Tourist, DTV. Get your timeline and checklist in minutes — chat now.";

export const Route = createFileRoute("/en/visa-wizard")({
  head: () => {
    const url = `${SITE}${PATH}`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/visa-wizard", "en")},
        { name: "description", content: ctaDesc(DESC, "/en/visa-wizard", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/visa-wizard", "en")},
        { property: "og:description", content: ctaDesc(DESC, "/en/visa-wizard", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "which Thailand visa, Thai visa eligibility, DTV vs LTR, Non-O retirement Thailand, work visa Thailand, Thailand visa wizard, digital nomad visa Thailand" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/visa-wizard", "/en/visa-wizard")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "@id": `${url}#tool`,
              name: "Thai Visa Eligibility Wizard",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Any",
              url,
              inLanguage: "en",
              offers: { "@type": "Offer", price: "0", priceCurrency: "THB" },
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
            }),
          },
          faqPageSchema([
            { q: "How does the Visa Eligibility Wizard work?", a: "Answer 5 short questions — purpose for staying in Thailand, stay length, work plans, whether you are 50+, and financial standing. The wizard scores all 7 long-stay Thai visas and recommends the best fit with reasons. It takes about 60 seconds." },
            { q: "Is this a substitute for a lawyer?", a: "The wizard provides a directional recommendation, not legal advice. Complex cases — criminal records, foreign-tax exposure, non-standard marital status — should be reviewed by NYC directly." },
            { q: "Is my data stored?", a: "No. The wizard is a fully client-side React component — all 5 answers run in your browser's local memory, are never sent to a server, are not written to localStorage or sessionStorage, and no identifying cookies (Google Analytics, Meta Pixel, etc.) are set on the wizard route. Refreshing the page clears every answer. GDPR/PDPA compliant by design." },
            { q: "DTV or LTR for digital nomads?", a: "Income < USD 80K/yr → DTV (5 yr, THB 10K fee, THB 500K bank proof). Income ≥ USD 80K/yr with a publicly-listed or qualifying employer → LTR Work-from-Thailand Professional (10 yr + Digital Work Permit + foreign-income tax exemption)." },
            { q: "Can I get Non-O Retirement under 50?", a: "No — Non-O Retirement requires age 50+. For long stays under 50, consider DTV, LTR (HSP/WFTP), Non-O Marriage (if married to a Thai), or Non-B + Work Permit." },
          ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Visa Wizard", item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: VisaWizardEnPage,
});

function VisaWizardEnPage() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-14 pb-10">
        <div className="mx-auto max-w-4xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span><span>Visa Wizard</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Sparkles className="mr-1 inline h-3 w-3" /> AI Eligibility Wizard · 5 questions · ~60 seconds
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">Which Thailand visa is right for you?</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Answer 5 quick questions and the wizard scores all 7 long-stay Thai visas (Tourist · DTV · LTR · Non-O Retirement · Non-O Marriage · Non-B + Work Permit · Non-ED) with reasons, fees, and timelines.
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">How does the wizard work?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Five questions on purpose, stay length, work plans, age, and financial standing. The wizard scores all 7 long-stay Thai visas in ~60 seconds with concrete reasons and financial thresholds. Everything runs in your browser — no data is sent to a server.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/en/visa-matrix" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              7-visa comparison <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-10">
        <div className="mx-auto max-w-3xl">
          <VisaWizard locale="en" />
          <p className="mt-4 text-center text-[11px] text-muted-foreground">
            ⚖️ This wizard provides a directional recommendation, not legal advice. Complex cases should be reviewed by NYC directly.
          </p>
        </div>
      </section>

      <WizardRelatedServices locale="en" />
      <TrustLayer variant="compact" />
          <SiteCoreLinks />
    </main>
  );
}
